import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase.js";
import { Button, Card, Field, Input, SegmentedControl } from "../lib/ui.js";
import { Numbers } from "./Numbers.js";
import { Sms } from "./Sms.js";

type Tab = "email" | "texts" | "numbers";
const TABS: { value: Tab; label: string }[] = [
  { value: "numbers", label: "Numbers" },
  { value: "texts", label: "Text messages" },
  { value: "email", label: "Email" },
];

/**
 * The sender for every email the platform sends (invoices and billing alerts, through Resend). One row in billing_settings
 * (migration 0025); the send-invoice and billing-alert Edge Functions read it each time, so nothing is hard coded.
 */
function EmailSettings() {
  const [saved, setSaved] = useState<{ from: string; replyTo: string } | null>(null);
  const [from, setFrom] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    void supabase
      .from("billing_settings")
      .select("email_from, email_reply_to")
      .eq("id", true)
      .single()
      .then(({ data }) => {
        const row = data as { email_from: string; email_reply_to: string } | null;
        const next = { from: row?.email_from ?? "", replyTo: row?.email_reply_to ?? "" };
        setSaved(next);
        setFrom(next.from);
        setReplyTo(next.replyTo);
      });
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setNote(null);
    const next = { from: from.trim(), replyTo: replyTo.trim() };
    const { error } = await supabase.from("billing_settings").update({ email_from: next.from, email_reply_to: next.replyTo }).eq("id", true);
    setSaving(false);
    if (error) {
      setNote({ ok: false, text: error.message });
      return;
    }
    setFrom(next.from);
    setReplyTo(next.replyTo);
    setSaved(next);
    setNote({ ok: true, text: "Saved." });
  }

  if (!saved) return <p className="rc-loading">Loading...</p>;

  return (
    <Card>
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 520 }}>
        <h2 className="rc-h2" style={{ margin: 0 }}>Email sender</h2>
        <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: 0 }}>
          Invoices and billing alerts are emailed through Resend from this address. It must be on a domain verified in Resend. Nothing is sent until it is filled in.
        </p>
        <Field label="From address">
          <Input type="email" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="billing@yourdomain.com" />
        </Field>
        <Field label="Reply-to address (optional)">
          <Input type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
        </Field>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Button type="submit" variant="primary" disabled={saving || (from.trim() === saved.from && replyTo.trim() === saved.replyTo)}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {note && <span style={{ fontSize: 13, color: note.ok ? "var(--rc-success)" : "var(--rc-danger)" }}>{note.text}</span>}
        </div>
      </form>
    </Card>
  );
}

/** Admin > Communication: how the platform talks to people -- the email sender, texts, and the phone numbers calls arrive on. */
export function Communication() {
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab");
  const tab: Tab = TABS.some((t) => t.value === asked) ? (asked as Tab) : "numbers";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <h1 className="rc-h1" style={{ margin: 0 }}>Communication</h1>
      <div>
        <SegmentedControl options={TABS} value={tab} onChange={(v) => setParams({ tab: v }, { replace: true })} />
      </div>
      {tab === "email" && <EmailSettings />}
      {tab === "texts" && <Sms />}
      {tab === "numbers" && <Numbers />}
    </div>
  );
}
